import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Lock, EyeOff, Eye, X } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { onUnauthorized } from '@/lib/api'

export default function AuthModal() {
  const [open, setOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login, logout } = useAuth()

  useEffect(() => {
    onUnauthorized(() => {
      logout()
      setOpen(true)
    })
  }, [logout])

  const close = useCallback(() => {
    setOpen(false)
    setEmail('')
    setPassword('')
    setError('')
    setShowPassword(false)
  }, [])

  const handleSubmit = async () => {
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      close()
    } catch (err: any) {
      setError(err.message || 'Нэвтрэх үед алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[440px] mx-4 p-8 flex flex-col gap-6">
        <button
          onClick={close}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
        >
          <X className="size-5" />
        </button>

        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold text-black">Нэвтрэх шаардлагатай</h2>
          <p className="text-sm text-gray-500">Энэ үйлдлийг хийхийн тулд нэвтэрнэ үү.</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3">{error}</div>
        )}

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">И-мэйл хаяг</label>
            <div className="bg-white border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs flex items-center gap-2 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 transition">
              <Mail className="size-[18px] text-gray-400 shrink-0" />
              <input
                type="email"
                placeholder="info@gmail.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-300 bg-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Нууц үг</label>
            <div className="bg-white border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs flex items-center gap-2 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 transition">
              <Lock className="size-[18px] text-gray-400 shrink-0" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-300 bg-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <Eye className="size-[18px]" /> : <EyeOff className="size-[18px]" />}
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-primary-500 text-white rounded-full px-[18px] py-3 text-base font-semibold shadow-xs hover:bg-primary-600 transition disabled:opacity-50"
        >
          {loading ? 'Нэвтэрч байна...' : 'Нэвтрэх'}
        </button>

        <p className="text-center text-sm text-gray-500">
          Бүртгэлгүй юу?{' '}
          <Link to="/register" onClick={close} className="text-primary-500 font-medium hover:text-primary-600">
            Бүртгүүлэх
          </Link>
        </p>
      </div>
    </div>
  )
}
